/**
 * Phase SC-2c — IA-side payroll (owner finances) API client.
 *
 * Wraps the real-auth tenant-self BE surface added in eduviaos-backend
 * (payroll domain, mounted at `/api/payroll/*`):
 *
 *   Pay runs:   POST/GET  /runs, GET /runs/summary,
 *               GET/PATCH/DELETE /runs/{id},
 *               POST /runs/{id}/finalize, POST /runs/{id}/reopen
 *   Payslips:   POST /runs/{id}/payslips,
 *               PATCH/DELETE /runs/{id}/payslips/{payslip_id}
 *
 * Tenant + actor are resolved BE-side from the JWT AuthContext; the vertical
 * is derived server-side from the tenant document. Every route is gated on
 * the `payroll` module + `view_payroll` (reads) / `manage_payroll` (writes).
 *
 * MONEY: every amount is integer PAISE on the wire (₹1,250.00 → 125000),
 * matching the BE. The UI converts rupees ↔ paise at the input edge only;
 * all state and math stay in paise. Wire shapes mirror
 * `app/domains/payroll/schemas.py` exactly — any field-shape change on the
 * BE must land here too.
 *
 * The write endpoints all return the *full* `PayRunDetailResponse` (run +
 * payslips) so a single response re-hydrates the whole workspace after any
 * mutation — no follow-up GET, no stale totals.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiError } from "@eduviaos/shared";
import { api } from "@/lib/api";

// Money helpers are shared with accounting — one source of truth for the
// rupee ↔ paise edge conversion and ₹ formatting.
export {
  formatPaise,
  formatPaiseCompact,
  paiseToRupees,
  rupeesToPaise,
} from "@/lib/accounting/api";

// ── Closed vocabularies (mirror schemas.py) ────────────────────────────

export type PayRunStatus = "draft" | "finalized";

// ── Wire shapes ────────────────────────────────────────────────────────

export interface PayRun {
  id: string;
  tenant_id: string;
  branch_id: string | null;
  year: number;
  month: number;
  status: string;
  gross_total_paise: number;
  deduction_total_paise: number;
  net_total_paise: number;
  payslip_count: number;
  finalized_at: string | null;
  finalized_by: string | null;
  note: string | null;
  created_at: string;
  updated_at: string;
}

export interface Payslip {
  id: string;
  tenant_id: string;
  pay_run_id: string;
  branch_id: string | null;
  staff_name: string;
  role_label: string;
  staff_payee_id: string | null;
  has_portal_access: boolean;
  gross_paise: number;
  deduction_paise: number;
  net_paise: number;
  note: string | null;
  created_at: string;
  updated_at: string;
}

export interface PayRunDetail {
  run: PayRun;
  payslips: Payslip[];
}

export interface PayRunListResponse {
  items: PayRun[];
  total: number;
}

export interface PayrollSummary {
  total_runs: number;
  draft_runs: number;
  finalized_runs: number;
  latest_finalized_net_paise: number;
  metadata: Record<string, unknown>;
}

export interface GeneratePayRunBody {
  year: number;
  month: number;
  branch_id?: string | null;
  note?: string | null;
}

export interface UpdatePayRunBody {
  note?: string | null;
}

export interface CreatePayslipBody {
  staff_name: string;
  role_label: string;
  gross_paise: number;
  deduction_paise?: number;
  staff_payee_id?: string | null;
  has_portal_access?: boolean;
  note?: string | null;
}

export type UpdatePayslipBody = {
  staff_name?: string;
  role_label?: string;
  gross_paise?: number;
  deduction_paise?: number;
  note?: string | null;
};

export interface PayRunListFilters {
  year?: number;
  status?: PayRunStatus;
  limit?: number;
  offset?: number;
}

// ── Labels ─────────────────────────────────────────────────────────────

export const PAY_RUN_STATUS_LABELS: Record<PayRunStatus, string> = {
  draft: "Draft",
  finalized: "Finalized",
};

/** `2026`, `7` → `July 2026`. */
export function payPeriodLabel(year: number, month: number): string {
  return new Date(year, month - 1, 1).toLocaleDateString("en-IN", {
    month: "long",
    year: "numeric",
  });
}

// ── React Query hooks ──────────────────────────────────────────────────

const RUNS_KEY = ["ia", "payroll", "runs"] as const;
const SUMMARY_KEY = ["ia", "payroll", "summary"] as const;
// The accounting P&L reads finalized payroll as its staff-cost line, so any
// payroll mutation must also bust the accounting P&L cache.
const ACCOUNTING_PNL_KEY = ["ia", "accounting", "pnl"] as const;

function toQuery(params: Record<string, string | number | boolean | undefined>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined) usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

export function usePayRuns(filters: PayRunListFilters) {
  return useQuery<PayRunListResponse>({
    queryKey: [...RUNS_KEY, filters],
    queryFn: () => api.get<PayRunListResponse>(`/api/payroll/runs${toQuery({ ...filters })}`),
    staleTime: 15_000,
  });
}

export function usePayRun(runId: string | null) {
  return useQuery<PayRunDetail>({
    queryKey: [...RUNS_KEY, "detail", runId],
    queryFn: () => api.get<PayRunDetail>(`/api/payroll/runs/${runId}`),
    enabled: runId !== null,
    staleTime: 10_000,
  });
}

export function usePayrollSummary() {
  return useQuery<PayrollSummary>({
    queryKey: SUMMARY_KEY,
    queryFn: () => api.get<PayrollSummary>("/api/payroll/runs/summary"),
    staleTime: 15_000,
  });
}

/** Invalidate everything a payroll mutation can touch (lists, detail,
 * summary, and the downstream accounting P&L). */
function useInvalidatePayroll() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: RUNS_KEY });
    void qc.invalidateQueries({ queryKey: SUMMARY_KEY });
    void qc.invalidateQueries({ queryKey: ACCOUNTING_PNL_KEY });
  };
}

export function useGeneratePayRun() {
  const invalidate = useInvalidatePayroll();
  return useMutation<PayRunDetail, ApiError, GeneratePayRunBody>({
    mutationFn: (body) => api.post<PayRunDetail>("/api/payroll/runs", body),
    onSuccess: invalidate,
  });
}

export function useUpdatePayRun() {
  const invalidate = useInvalidatePayroll();
  return useMutation<PayRunDetail, ApiError, { id: string; body: UpdatePayRunBody }>({
    mutationFn: ({ id, body }) => api.patch<PayRunDetail>(`/api/payroll/runs/${id}`, body),
    onSuccess: invalidate,
  });
}

export function useFinalizePayRun() {
  const invalidate = useInvalidatePayroll();
  return useMutation<PayRunDetail, ApiError, string>({
    mutationFn: (id) => api.post<PayRunDetail>(`/api/payroll/runs/${id}/finalize`, {}),
    onSuccess: invalidate,
  });
}

export function useReopenPayRun() {
  const invalidate = useInvalidatePayroll();
  return useMutation<PayRunDetail, ApiError, string>({
    mutationFn: (id) => api.post<PayRunDetail>(`/api/payroll/runs/${id}/reopen`, {}),
    onSuccess: invalidate,
  });
}

export function useDeletePayRun() {
  const invalidate = useInvalidatePayroll();
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => api.delete<void>(`/api/payroll/runs/${id}`),
    onSuccess: invalidate,
  });
}

export function useAddPayslip() {
  const invalidate = useInvalidatePayroll();
  return useMutation<PayRunDetail, ApiError, { runId: string; body: CreatePayslipBody }>({
    mutationFn: ({ runId, body }) =>
      api.post<PayRunDetail>(`/api/payroll/runs/${runId}/payslips`, body),
    onSuccess: invalidate,
  });
}

export function useUpdatePayslip() {
  const invalidate = useInvalidatePayroll();
  return useMutation<
    PayRunDetail,
    ApiError,
    { runId: string; payslipId: string; body: UpdatePayslipBody }
  >({
    mutationFn: ({ runId, payslipId, body }) =>
      api.patch<PayRunDetail>(`/api/payroll/runs/${runId}/payslips/${payslipId}`, body),
    onSuccess: invalidate,
  });
}

export function useDeletePayslip() {
  const invalidate = useInvalidatePayroll();
  return useMutation<PayRunDetail, ApiError, { runId: string; payslipId: string }>({
    mutationFn: ({ runId, payslipId }) =>
      api.delete<PayRunDetail>(`/api/payroll/runs/${runId}/payslips/${payslipId}`),
    onSuccess: invalidate,
  });
}
